import { useTranslation } from 'react-i18next';
import { useAiSidebar } from '@anytag/shared-atoms';
import {
  AIModelName,
  useCloseGeminiThreadMutation,
  useCreateGeminiThreadMutation,
  useCreateOpenAiThreadMutation,
  useDeleteOpenAiThreadMutation
} from '@anytag/graphql';

interface Props {
  isOpenAiModel?: boolean;
  onCreateError: (message: any) => void;
}

export const useManageChatThread = ({ onCreateError, isOpenAiModel = true }: Props) => {
  const { t } = useTranslation();
  const { threadId, isChatHistoryOpen, isAiSidebarOpen, setAiAssistantState } = useAiSidebar();
  const { callDeleteOpenAiThread, loading: isDeletingOpenAiThread } = useDeleteOpenAiThreadMutation();
  const { callCreateOpenAiThread, loading: isCreatingOpenAiThread } = useCreateOpenAiThreadMutation();
  const { callCloseGeminiThread, loading: isClosingGeminiThread } = useCloseGeminiThreadMutation();
  const { callCreateGeminiThread, loading: isCreatingGeminiThread } = useCreateGeminiThreadMutation();

  const createChatAiThread = async (aiModel?: AIModelName) => {
    if (!!threadId || isCreatingOpenAiThread || isCreatingGeminiThread) {
      return;
    }

    try {
      let openAiThreadId;

      if (typeof aiModel !== 'undefined' ? aiModel === AIModelName.OPENAI : isOpenAiModel) {
        const { data } = await callCreateOpenAiThread();
        openAiThreadId = data?.createOpenAiThread?.threadId ?? null;
      } else {
        const { data } = await callCreateGeminiThread();
        openAiThreadId = data?.createGeminiThread?.threadId ?? null;
      }

      if (openAiThreadId) {
        setAiAssistantState({ threadId: openAiThreadId });
      } else {
        onCreateError?.(t<string>('Can not get threadId after created'));
      }
    } catch (error) {
      onCreateError?.(error.message);
    }
  };

  const closeChatAiThread = async () => {
    if (!threadId || isDeletingOpenAiThread || isClosingGeminiThread) {
      return;
    }

    try {
      if (isOpenAiModel) {
        await callDeleteOpenAiThread({ variables: { input: { id: threadId } } });
      } else {
        await callCloseGeminiThread({ variables: { input: { id: threadId } } });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    closeChatAiThread,
    createChatAiThread,
    isAiSidebarOpen,
    isChatHistoryOpen,
    setAiAssistantState,
    threadId,
    isCreatingChatAi: isCreatingOpenAiThread || isCreatingGeminiThread,
    isDeletingChatAi: isDeletingOpenAiThread || isClosingGeminiThread
  };
};
